import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingOverlay from './components/LoadingOverlay';
import SeoManager from './components/SeoManager';
import AuthPage from './views/AuthPage';

// Views
import HomeView from './views/HomeView';
import AboutUs from './views/AboutUs';
import ContactUs from './views/ContactUs';
import Vehicles from './views/Vehicles';
import CompareVehicles from './views/CompareVehicles';
import BookingReview from './views/BookingReview';
import ProductDetails from './views/Product/ShowDetails';
import Cart from './views/Cart';
import Wishlist from './views/Product/Wishlist';
import Checkout from './views/Checkout/Checkout';
import Admin from './views/Admin';
import AdminBookings from './views/AdminBookings';
import AdminAccess from './views/AdminAccess';
import UserDashboard from './views/UserDashboard';
import AdminCategory from './views/Category/AdminCategory';
import AddCategory from './views/Category/AddCategory';
import EditCategory from './views/Category/EditCategory';
import AdminProduct from './views/Product/AdminProduct';
import AddProduct from './views/Product/AddProduct';
import EditProduct from './views/Product/EditProduct';
import Users from './views/Users';
import AdminUserForm from './views/AdminUserForm';
import AdminMessages from './views/AdminMessages';
import AdminMessageForm from './views/AdminMessageForm';
import ListProducts from './views/Category/ListProducts';
import TermsAndConditions from './views/TermsAndConditions';
import PaymentSuccess from './views/payment/Success';
import PaymentFailed from './views/payment/Failed';
import MeetTheTeam from './views/MeetTheTeam';
import LeaseYourVehicle from './views/LeaseYourVehicle';
import NotFound from './views/NotFound';

// API
import { categoryAPI, productAPI, cartAPI, authAPI, setAuthToken, clearAuthToken, getAuthToken, getUserRole } from './config/api';
import { showAppToast } from './utils/notifications';

const ROUTE_TRANSITION_PRELOADER_MS = 420;
const FULL_ADMIN_SCOPE = 'platform.full_access';
const ADMIN_DASHBOARD_ROLES = new Set([
  'admin',
  'fleet_manager',
  'reservations_manager',
  'support_agent',
  'account_manager',
]);

function isAdminDashboardRole(role) {
  return ADMIN_DASHBOARD_ROLES.has((role || '').toLowerCase());
}

function hasAdminScope(user, fallbackRole, scope) {
  if (!scope) return isAdminDashboardRole(user?.role || fallbackRole);
  const role = (user?.role || fallbackRole || '').toLowerCase();
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  if (role === 'admin' && !permissions.length) return true;
  return permissions.includes(FULL_ADMIN_SCOPE) || permissions.includes(scope);
}

function shouldUseRoutePreloader(/* pathname */) {
  // Fire the preloader on every route change.
  // Dashboard-internal tab switches don't change the URL pathname,
  // so they never trigger this.
  return true;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, [pathname]);

  return null;
}

export function RouteTransitionPreloader({ disabled, theme }) {
  const { pathname, search } = useLocation();
  const locationSignature = `${pathname}${search}`;
  const previousLocationRef = useRef(locationSignature);
  const transitionTimerRef = useRef(null);
  const [isRouteTransitioning, setIsRouteTransitioning] = useState(false);

  const clearRouteTransitionTimer = useCallback(() => {
    if (transitionTimerRef.current) {
      window.clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
  }, []);

  const startRouteTransition = useCallback((nextPathname = pathname) => {
    if (disabled || !shouldUseRoutePreloader(nextPathname)) return;

    clearRouteTransitionTimer();
    setIsRouteTransitioning(true);

    transitionTimerRef.current = window.setTimeout(() => {
      setIsRouteTransitioning(false);
      transitionTimerRef.current = null;
    }, ROUTE_TRANSITION_PRELOADER_MS);
  }, [clearRouteTransitionTimer, disabled, pathname]);

  useEffect(() => {
    if (disabled) {
      clearRouteTransitionTimer();
      previousLocationRef.current = locationSignature;
      setIsRouteTransitioning(false);
      return undefined;
    }

    if (previousLocationRef.current !== locationSignature) {
      previousLocationRef.current = locationSignature;
      startRouteTransition(pathname);
    }

    return undefined;
  }, [clearRouteTransitionTimer, disabled, locationSignature, pathname, startRouteTransition]);

  useEffect(() => {
    if (disabled) return undefined;

    const handleInternalRouteClick = (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }

      const target = event.target;
      const targetElement = target instanceof Element ? target : target?.parentElement;
      const anchor = targetElement?.closest?.('a[href]');

      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;

      const nextUrl = new URL(anchor.href, window.location.href);
      const nextSignature = `${nextUrl.pathname}${nextUrl.search}`;

      if (nextUrl.origin === window.location.origin && nextSignature !== locationSignature && shouldUseRoutePreloader(nextUrl.pathname)) {
        startRouteTransition(nextUrl.pathname);
      }
    };

    document.addEventListener('click', handleInternalRouteClick, true);
    return () => document.removeEventListener('click', handleInternalRouteClick, true);
  }, [disabled, locationSignature, startRouteTransition]);

  useEffect(() => () => clearRouteTransitionTimer(), [clearRouteTransitionTimer]);

  if (disabled || !isRouteTransitioning) return null;

  return <LoadingOverlay theme={theme} variant="route" />;
}

function AppChrome({
  children,
  cartCount,
  userRole,
  currentUser,
  onLogout,
  onOpenAuthModal,
  theme,
  onToggleTheme,
}) {
  const { pathname } = useLocation();
  const isDashboardRoute = pathname.startsWith('/admin') || pathname.startsWith('/dashboard');

  return (
    <>
      {!isDashboardRoute && (
        <Navbar
          cartCount={cartCount}
          userRole={userRole}
          currentUser={currentUser}
          onLogout={onLogout}
          onOpenAuthModal={onOpenAuthModal}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />
      )}

      <main className={isDashboardRoute ? 'dashboard-route-main' : ''} style={{ minHeight: isDashboardRoute ? '100vh' : '60vh' }}>
        {children}
      </main>

      {!isDashboardRoute && <Footer />}

      {!isDashboardRoute && (
        <a
          href="https://wa.me/263712768037?text=Hi%2C%20I%27m%20interested%20in%20renting%20a%20vehicle.%20Can%20you%20help%20me%3F"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          aria-label="Chat on WhatsApp"
        >
          <i className="bi bi-whatsapp"></i>
        </a>
      )}
    </>
  );
}

function App() {
  // State management
  const [categories, setCategories] = useState(null);
  const [products, setProducts] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [userCartItems, setUserCartItems] = useState(null);
  const [userTotalCost, setUserTotalCost] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isBackendLoading, setIsBackendLoading] = useState(false);
  const [backendBootFailed, setBackendBootFailed] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const fetchDataRequestId = useRef(0);
  const isAdmin = isAdminDashboardRole(userRole);
  const canAccessAdminScope = (scope) => (
    isAdmin && hasAdminScope(currentUser, userRole, scope)
  );

  // Fetch categories and products
  const fetchData = useCallback(async () => {
    const requestId = fetchDataRequestId.current + 1;
    fetchDataRequestId.current = requestId;

    try {
      setIsBackendLoading(true);
      const [categoriesRes, productsRes] = await Promise.all([
        categoryAPI.list(),
        productAPI.list()
      ]);

      if (requestId !== fetchDataRequestId.current) return;

      const publicCategories = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
      const publicProducts = Array.isArray(productsRes.data) ? productsRes.data : [];

      setCategories(publicCategories);
      setProducts(publicProducts);
      setIsBackendLoading(false);
      setBackendBootFailed(false);
    } catch (error) {
      if (requestId !== fetchDataRequestId.current) return;

      console.error('Failed to fetch data:', error);
      setIsBackendLoading(false);
      setBackendBootFailed(true);
    }
  }, []);

  // Fetch user cart items
  const fetchUserCartInfo = useCallback(async () => {
    try {
      const response = await cartAPI.list({ suppressUnauthorizedRedirect: true });
      const result = response.data;

      if (result.totalCost && result.cartItems) {
        setUserCartItems(result.cartItems);
        setUserTotalCost(result.totalCost.toFixed(2));
        setCartCount(result.cartItems.length);
      } else {
        setUserCartItems(null);
        setUserTotalCost(null);
        setCartCount(0);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        // Session expired
        clearAuthToken();
        setUserRole(null);
        setCurrentUser(null);
      }
      console.error('Failed to fetch cart:', error);
    }
  }, []);

  // Open auth page
  const openAuthModal = (tab = 'login') => {
    window.location.href = `/auth?mode=${tab}`;
  };

  // Handle successful login
  const handleLoginSuccess = (token, role, user = null) => {
    const normalizedRole = role?.toLowerCase() || 'user';
    setCurrentUser(user || null);
    setAuthToken(token, normalizedRole);
    setUserRole(normalizedRole);
    fetchUserCartInfo();
    showAppToast({ message: 'Welcome back!', type: 'success' });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthToken();
      setUserRole(null);
      setCurrentUser(null);
      setUserCartItems(null);
      setCartCount(0);
      window.location.href = '/';
    }
  };

  // Reset cart count
  const resetCartCount = () => {
    setCartCount(0);
  };

  const toggleTheme = () => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const handleDashboardThemeChange = (event) => {
      const nextTheme = event.detail?.theme || document.documentElement.getAttribute('data-theme');
      if (nextTheme && nextTheme !== theme) {
        setTheme(nextTheme);
      }
    };

    window.addEventListener('deroyalty-theme-change', handleDashboardThemeChange);
    return () => window.removeEventListener('deroyalty-theme-change', handleDashboardThemeChange);
  }, [theme]);

  // Initial data fetch
  useEffect(() => {
    let isMounted = true;
    fetchData();

    // Check if user is already logged in (FIX-09: use in-memory token)
    const token = getAuthToken();
    const role = getUserRole();

    if (token && role) {
      setUserRole(role.toLowerCase());
      (async () => {
        try {
          const res = await authAPI.getCurrentUser({ suppressUnauthorizedRedirect: true });
          if (isMounted && res.data?.user) {
            const u = res.data.user;
            setCurrentUser(u);
            const restoredRole = (u.role || role || 'user').toLowerCase();
            setAuthToken(token, restoredRole);
            setUserRole(restoredRole);
            fetchUserCartInfo();
          }
        } catch (e) {
          if (isMounted) {
            clearAuthToken();
            setUserRole(null);
            setCurrentUser(null);
          }
        } finally {
          if (isMounted) {
            setAuthChecked(true);
          }
        }
      })();
    } else {
      // Try to restore session from HttpOnly cookie via API
      (async () => {
        try {
          const res = await authAPI.getCurrentUser({ suppressUnauthorizedRedirect: true });
          if (isMounted && res.data?.user) {
            const u = res.data.user;
            setCurrentUser(u);
            setAuthToken(null, u.role);
            setUserRole((u.role || 'user').toLowerCase());
            fetchUserCartInfo();
          }
        } catch (e) {
          // Not logged in - that is fine
        } finally {
          if (isMounted) {
            setAuthChecked(true);
          }
        }
      })();
    }

    return () => {
      isMounted = false;
    };
  }, [fetchData, fetchUserCartInfo]);

  // FIX-21: Periodic role verification from backend (defense-in-depth)
  useEffect(() => {
    if (!userRole) return;
    authAPI.getCurrentUser({ suppressUnauthorizedRedirect: true })
      .then(response => {
        const serverUser = response.data?.user;
        if (serverUser) setCurrentUser(serverUser);
        const serverRole = serverUser?.role?.toLowerCase();
        if (serverRole && serverRole !== userRole) {
          setAuthToken(getAuthToken(), serverRole);
          setUserRole(serverRole);
        }
      })
      .catch(() => {
        clearAuthToken();
        setUserRole(null);
        setCurrentUser(null);
      });
  }, [userRole]);

  // Retry backend connection
  const retryBoot = () => {
    setBackendBootFailed(false);
    fetchData();
  };

  const isBooting = isBackendLoading || backendBootFailed || !authChecked || !categories || !products;
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const appContent = (
    <Router>
      <div className="App">
        <ScrollToTop />
        <SeoManager products={products || []} categories={categories || []} />

        {isBooting && (
          <LoadingOverlay
            isLoading={isBackendLoading}
            isFailed={backendBootFailed}
            onRetry={retryBoot}
            theme={theme}
          />
        )}

        <RouteTransitionPreloader disabled={isBooting} theme={theme} />

        {!isBooting && (
          <AppChrome
            cartCount={cartCount}
            userRole={userRole}
            currentUser={currentUser}
            onLogout={handleLogout}
            onOpenAuthModal={openAuthModal}
            theme={theme}
            onToggleTheme={toggleTheme}
          >
            {categories && products && (
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route
                  path="/home"
                  element={
                    <HomeView
                      categories={categories}
                      products={products}
                      userRole={userRole}
                      onOpenAuthModal={openAuthModal}
                    />
                  }
                />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/quote" element={<ContactUs requestType="quote" />} />
                <Route path="/meet-the-team" element={<MeetTheTeam />} />
                <Route path="/lease-your-vehicle" element={<LeaseYourVehicle />} />
                <Route
                  path="/vehicles"
                  element={
                    <Vehicles
                      products={products}
                      categories={categories}
                    />
                  }
                />
                <Route
                  path="/vehicles/compare"
                  element={
                    <CompareVehicles
                      products={products}
                      categories={categories}
                    />
                  }
                />
                <Route
                  path="/vehicle/show/:id/:name"
                  element={
                    <ProductDetails
                      products={products}
                      userRole={userRole}
                      onOpenAuthModal={openAuthModal}
                    />
                  }
                />
                <Route
                  path="/category/show/:id"
                  element={
                    <ListProducts
                      products={products}
                      categories={categories}
                    />
                  }
                />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route
                  path="/booking/review"
                  element={<BookingReview userRole={userRole} />}
                />

                {/* Auth Page */}
                <Route
                  path="/auth"
                  element={<AuthPage onLoginSuccess={handleLoginSuccess} />}
                />

                {/* Protected User Routes */}
                <Route
                  path="/cart"
                  element={
                    userRole ? (
                      <Cart
                        cartItems={userCartItems}
                        totalCost={userTotalCost}
                        onUpdate={fetchUserCartInfo}
                      />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    userRole ? (
                      <Wishlist userRole={userRole} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/checkout"
                  element={
                    userRole ? (
                      <Checkout
                        cartItems={userCartItems}
                        totalCost={userTotalCost}
                        onResetCart={resetCartCount}
                      />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />

                {/* Payment Routes */}
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/failed" element={<PaymentFailed />} />

                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    isAdmin ? (
                      <Admin categories={categories} products={products} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/bookings"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="bookings"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/wishlist"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="wishlist"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/checkout"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="checkout"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/fleet"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="fleet"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/fleet/:id/:name"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="vehicle"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/support"
                  element={
                    userRole ? (
                      isAdmin ? (
                        <Navigate to="/admin" replace />
                      ) : (
                        <UserDashboard
                          activeView="support"
                          cartItems={userCartItems}
                          totalCost={userTotalCost}
                          products={products}
                          currentUser={currentUser}
                          onCartUpdate={fetchUserCartInfo}
                        />
                      )
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/bookings"
                  element={
                    canAccessAdminScope('bookings.manage') ? (
                      <AdminBookings products={products} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/category"
                  element={
                    canAccessAdminScope('categories.manage') ? (
                      <AdminCategory categories={categories} onUpdate={fetchData} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/category/add"
                  element={
                    canAccessAdminScope('categories.manage') ? (
                      <AddCategory onSuccess={fetchData} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/category/:id"
                  element={
                    canAccessAdminScope('categories.manage') ? (
                      <EditCategory categories={categories} onUpdate={fetchData} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/vehicles"
                  element={
                    canAccessAdminScope('fleet.manage') ? (
                      <AdminProduct products={products} onUpdate={fetchData} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/vehicle/new"
                  element={
                    canAccessAdminScope('fleet.manage') ? (
                      <AddProduct categories={categories} onSuccess={fetchData} />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/vehicle/:id"
                  element={
                    canAccessAdminScope('fleet.manage') ? (
                      <EditProduct
                        products={products}
                        categories={categories}
                        onUpdate={fetchData}
                      />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    canAccessAdminScope('users.manage') ? (
                      <Users />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/users/new"
                  element={
                    canAccessAdminScope('users.manage') ? (
                      <AdminUserForm />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/users/:id/edit"
                  element={
                    canAccessAdminScope('users.manage') ? (
                      <AdminUserForm />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/access"
                  element={
                    canAccessAdminScope('users.manage') ? (
                      <AdminAccess />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/messages"
                  element={
                    canAccessAdminScope('messages.manage') ? (
                      <AdminMessages />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/messages/new"
                  element={
                    canAccessAdminScope('messages.manage') ? (
                      <AdminMessageForm />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />
                <Route
                  path="/admin/messages/:id/edit"
                  element={
                    canAccessAdminScope('messages.manage') ? (
                      <AdminMessageForm />
                    ) : (
                      <Navigate to="/home" replace />
                    )
                  }
                />

                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            )}
          </AppChrome>
        )}
      </div>
    </Router>
  );

  return googleClientId
    ? <GoogleOAuthProvider clientId={googleClientId}>{appContent}</GoogleOAuthProvider>
    : appContent;
}

export default App;
